<template>
  <button
    class="base-button"
    :class="[
      `base-button--${type}`,
      `base-button--${size}`,
      {
        'base-button--disabled': disabled,
        'base-button--loading': loading,
        'base-button--block': block,
        'base-button--round': round,
        'base-button--circle': circle,
      },
    ]"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <span v-if="loading" class="base-button__loading">
      <svg class="base-button__loading-icon" viewBox="0 0 24 24">
        <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
      </svg>
    </span>
    <span class="base-button__content">
      <slot></slot>
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'BaseButton',
  props: {
    type: {
      type: String as () =>
        | 'primary'
        | 'success'
        | 'warning'
        | 'danger'
        | 'info'
        | 'text',
      default: 'primary',
    },
    size: {
      type: String as () => 'large' | 'medium' | 'small' | 'mini',
      default: 'medium',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    round: {
      type: Boolean,
      default: false,
    },
    circle: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const handleClick = (event: MouseEvent) => {
      if (!props.disabled && !props.loading) {
        emit('click', event);
      }
    };

    return {
      handleClick,
    };
  },
});
</script>

<style scoped>
.base-button {
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  background: #fff;
  /* border: 1px solid #dcdfe6; */
  color: #606266;
  text-align: center;
  box-sizing: border-box;
  outline: none;
  margin: 0;
  transition: all 0.1s;
  font-weight: 500;
  user-select: none;
  vertical-align: middle;
  padding: 12px 20px;
  font-size: 14px;
  border-radius: var(--border-radius);
}

.base-button:hover {
  color: #409eff;
  /* border-color: #c6e2ff; */
  background-color: #ecf5ff;
}

.base-button:active {
  color: #3a8ee6;
  /* border-color: #3a8ee6; */
  outline: none;
}

.base-button:focus {
  outline: none;
}

/* 按钮类型 */
.base-button--primary {
  color: var(--text-color-white);
  /*background: linear-gradient(94deg, #A065FF 0%, #4B8FF9 62%, #24D692 100%);*/
  background: var(--bg-color-primary);
}

.base-button--primary:hover {
  /*border-color: linear-gradient(94deg, #A065FF 0%, #4B8FF9 62%, #24D692 100%);*/
  background: var(--bg-color-primary);
  color: var(--text-color-white);
  opacity: 0.8;
}

.base-button--success {
  color: #fff;
  background-color: #67c23a;
  /* border-color: #67c23a; */
}

.base-button--warning {
  color: #fff;
  background-color: #e6a23c;
  /* border-color: #e6a23c; */
}

.base-button--danger {
  color: #fff;
  background-color: #f56c6c;
  /* border-color: #f56c6c; */
}

.base-button--info {
  color: #fff;
  background-color: #909399;
  /* border-color: #909399; */
}

.base-button--text {
  color: #409eff;
  background: transparent;
  /* border-color: transparent; */
}

/* 按钮尺寸 */
.base-button--large {
  padding: 14px 24px;
  font-size: 16px;
  border-radius: var(--border-radius);
}

.base-button--medium {
  padding: 12px 20px;
  font-size: 14px;
  border-radius: var(--border-radius);
}

.base-button--small {
  padding: 9px 15px;
  font-size: 12px;
  border-radius: var(--border-radius);
}

.base-button--mini {
  padding: 7px 15px;
  font-size: 12px;
  border-radius: var(--border-radius);
}

/* 禁用状态 */
.base-button--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* 加载状态 */
.base-button--loading {
  pointer-events: none;
  opacity: 0.8;
}

.base-button__loading {
  display: inline-flex;
  margin-right: 6px;
  animation: rotating 2s linear infinite;
}

.base-button__loading-icon {
  width: 1em;
  height: 1em;
  fill: currentColor;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* 块级按钮 */
.base-button--block {
  display: block;
  width: 100%;
}

/* 圆角按钮 */
.base-button--round {
  border-radius: var(--border-radius);
}

/* 圆形按钮 */
.base-button--circle {
  border-radius: var(--border-radius);
  padding: 12px;
}
</style>
